import { ControllerStatus } from '#models/ControllerStatus.enum';
import { ControllerCode } from '#models/MicroController.model';
import { formatDate } from '#utils/dateUtils';

interface CheckRequest {
  requestDateTime: string;
  requestorId: string;
  controlData: Record<string, unknown> & {
    requestData?: Record<string, unknown> & {
      buCode?: string;
    };
  };
  requestDocumentIds?: string[];
}

type EscalationRequest = CheckRequest & {
  controlData: Record<string, unknown> & {
    requestData?: Record<string, unknown> & {
      comment?: string;
      buCode?: string;
    };
  };
  responseDocumentIds?: string[];
};

type EscalationResponse = EscalationRequest & {
  controlData: Record<string, unknown> & {
    escalationContact?: string;
    escalationResponse: { validator: string; comment: string };
  };
  expirationDateTime: string;
  responseDateTime: string;
  responseDocumentIds?: string[];
};

type CheckResponse = CheckRequest & {
  controlData: Record<string, unknown> & {
    escalationContact?: string;
    comment?: string;
    rejectCode?: string;
    requestData?: Record<string, unknown> & {
      comment?: string;
      buCode?: string;
    };
  };
  expirationDateTime: string;
  responseDateTime: string;
  responseDocumentIds?: string[];
};

type CheckErrorResponse = CheckResponse & {
  controlData: Record<string, unknown> & {
    errorDetails: string;
    escalationContact?: string;
    requestData?: Record<string, unknown> & {
      comment?: string;
      buCode?: string;
    };
  };
};

type EscalationErrorResponse = EscalationRequest & {
  responseDateTime: string;
  controlData: Record<string, unknown> & {
    errorDetails: string;
    requestData?: Record<string, unknown> & {
      comment?: string;
      buCode?: string;
    };
  };
};

export interface Validation {
  validationDateTime: string;
  requestorId: string;
}

export type CheckOrEscalationResponse = CheckRequest & {
  controlData: Record<string, unknown> & { expirationDateTime: string; escalationContact?: string };
  responseDate: string;
};

export type ControlDetail = CheckOrEscalationResponse | Validation;

export type ControllerDetailsWithEscalationResponse<T extends ControllerCode = ControllerCode> = { controlCode: T } & (
  | {
      statusCode: ControllerStatus.ESCALATION_ACCEPTED | ControllerStatus.ESCALATION_REJECTED;
    }
  | {
      statusCode:
        | ControllerStatus.ACKNOWLEDGED_WITH_ESCALATION_ACCEPTED
        | ControllerStatus.ACKNOWLEDGED_WITH_ESCALATION_REJECTED;
      validation: Validation;
    }
) & {
    check: CheckResponse;
    escalation: EscalationResponse;
  };

type ControllerDetailsWithEscalation<T extends ControllerCode> =
  | {
      controlCode: T;
      statusCode: ControllerStatus.ESCALATING;
      check: CheckResponse;
      escalation: EscalationRequest;
    }
  | ControllerDetailsWithEscalationResponse<T>;

interface ControllerDetailsWithValidation<T extends ControllerCode> {
  controlCode: T;
  statusCode: ControllerStatus.ACKNOWLEDGED_WITH_CHECK_SUCCESS;
  check: CheckResponse;
  validation: Validation;
}

export type ControllerDetailsWithResponse<T extends ControllerCode = ControllerCode> =
  | ({ controlCode: T } & (
      | {
          statusCode:
            | ControllerStatus.CHECK_SUCCESS
            | ControllerStatus.CHECK_REJECTED
            | ControllerStatus.MANUAL_CHECK_REQUIRED;
          check: CheckResponse;
        }
      | {
          statusCode: ControllerStatus.ESCALATION_ERROR;
          check: CheckResponse;
          escalation: EscalationErrorResponse;
        }
    ))
  | ControllerDetailsWithValidation<T>
  | ControllerDetailsWithEscalation<T>;

export type ControllerDetailsStarted<T extends ControllerCode = ControllerCode> =
  | ({ controlCode: T } & (
      | {
          statusCode: ControllerStatus.CHECKING;
          check: CheckRequest;
        }
      | {
          statusCode: ControllerStatus.CHECK_ERROR;
          check: CheckErrorResponse;
        }
      | {
          statusCode: ControllerStatus.ESCALATION_ERROR;
          check: CheckResponse;
          escalation: EscalationErrorResponse;
        }
    ))
  | ControllerDetailsWithResponse<T>;

export type ControllerDetails<T extends ControllerCode = ControllerCode> =
  | ({ controlCode: T } & { statusCode: ControllerStatus.NOT_STARTED })
  | ControllerDetailsStarted<T>;

export const checkControlDetailsCode = <T extends ControllerCode>(
  controlDetails: ControllerDetails,
  code: T,
): controlDetails is ControllerDetails<T> => {
  return controlDetails.controlCode === code;
};

export const findControl = <T extends ControllerCode>(
  controlsDetails: ControllerDetails[],
  code: T,
): ControllerDetails<T> | undefined => {
  return controlsDetails.find<ControllerDetails<T>>((control) => checkControlDetailsCode(control, code));
};

export function hasCheckData<T extends ControllerCode = ControllerCode>(
  controllerDetails: ControllerDetails<T>,
): controllerDetails is ControllerDetailsWithResponse<T> {
  return !!(
    controllerDetails.statusCode !== ControllerStatus.NOT_STARTED &&
    controllerDetails.statusCode !== ControllerStatus.CHECKING &&
    controllerDetails.statusCode !== ControllerStatus.CHECK_ERROR &&
    controllerDetails.check
  );
}

export function isEscalated<T extends ControllerCode = ControllerCode>(
  controllerDetails: ControllerDetails<T>,
): controllerDetails is ControllerDetailsWithEscalation<T> {
  return (
    controllerDetails.statusCode === ControllerStatus.ESCALATING ||
    controllerDetails.statusCode === ControllerStatus.ESCALATION_ACCEPTED ||
    controllerDetails.statusCode === ControllerStatus.ESCALATION_REJECTED ||
    controllerDetails.statusCode === ControllerStatus.ACKNOWLEDGED_WITH_ESCALATION_ACCEPTED ||
    controllerDetails.statusCode === ControllerStatus.ACKNOWLEDGED_WITH_ESCALATION_REJECTED
  );
}

export function hasEscalation<T extends ControllerCode = ControllerCode>(
  controllerDetails: ControllerDetails<T>,
): controllerDetails is ControllerDetailsWithEscalation<T> {
  return isEscalated(controllerDetails) || controllerDetails.statusCode === ControllerStatus.ESCALATION_ERROR;
}

export const aggregateControllerDocuments = (controllerDetails: ControllerDetails) => {
  return [
    ...(hasCheckData(controllerDetails) ? getResponseDocumentIds(controllerDetails.check) : []),
    ...(hasEscalation(controllerDetails) ? getResponseDocumentIds(controllerDetails.escalation) : []),
    ...(hasEscalation(controllerDetails) ? getRequestDocumentIds(controllerDetails.escalation) : []),
  ];
};

export const getResponseDocumentIds = (response: CheckResponse | EscalationRequest | EscalationResponse): string[] => {
  return response.responseDocumentIds ?? [];
};

export const getRequestDocumentIds = (request: EscalationRequest): string[] => {
  return request.requestDocumentIds ?? [];
};

export const getLastActionOwner = (controllerDetails: ControllerDetails) => {
  switch (controllerDetails.statusCode) {
    case ControllerStatus.NOT_STARTED:
      return;
    case ControllerStatus.CHECKING:
    case ControllerStatus.CHECK_ERROR:
    case ControllerStatus.CHECK_SUCCESS:
    case ControllerStatus.CHECK_REJECTED:
    case ControllerStatus.MANUAL_CHECK_REQUIRED:
      return controllerDetails.check.requestorId;
    case ControllerStatus.ESCALATING:
    case ControllerStatus.ESCALATION_ERROR:
      return controllerDetails.escalation.requestorId;
    case ControllerStatus.ESCALATION_ACCEPTED:
    case ControllerStatus.ESCALATION_REJECTED:
      return controllerDetails.escalation.controlData.escalationResponse.validator;
    case ControllerStatus.ACKNOWLEDGED_WITH_CHECK_SUCCESS:
    case ControllerStatus.ACKNOWLEDGED_WITH_ESCALATION_REJECTED:
    case ControllerStatus.ACKNOWLEDGED_WITH_ESCALATION_ACCEPTED:
      return controllerDetails.validation.requestorId;
    default:
      return controllerDetails satisfies never;
  }
};

export const getReviewDate: (controllerDetails: ControllerDetails) => string | undefined = (
  controllerDetails: ControllerDetails,
) => {
  switch (controllerDetails.statusCode) {
    case ControllerStatus.NOT_STARTED:
      return;
    case ControllerStatus.CHECKING:
    case ControllerStatus.CHECK_ERROR:
      return formatDate(controllerDetails.check.requestDateTime);
    case ControllerStatus.MANUAL_CHECK_REQUIRED:
    case ControllerStatus.CHECK_SUCCESS:
    case ControllerStatus.CHECK_REJECTED:
      return formatDate(controllerDetails.check.responseDateTime);
    case ControllerStatus.ESCALATING:
    case ControllerStatus.ESCALATION_ERROR:
      return formatDate(controllerDetails.escalation.requestDateTime);
    case ControllerStatus.ESCALATION_REJECTED:
    case ControllerStatus.ESCALATION_ACCEPTED:
      return formatDate(controllerDetails.escalation.responseDateTime);
    case ControllerStatus.ACKNOWLEDGED_WITH_CHECK_SUCCESS:
    case ControllerStatus.ACKNOWLEDGED_WITH_ESCALATION_REJECTED:
    case ControllerStatus.ACKNOWLEDGED_WITH_ESCALATION_ACCEPTED:
      return formatDate(controllerDetails.validation.validationDateTime);
  }
};

export const getEscalationContact = (controllerDetails: ControllerDetails) => {
  return hasCheckData(controllerDetails) ? controllerDetails.check.controlData.escalationContact : undefined;
};

export const isEscalationAnswered = (
  controllerDetails: ControllerDetails,
): controllerDetails is ControllerDetailsWithEscalationResponse => {
  return (
    controllerDetails.statusCode === ControllerStatus.ESCALATION_ACCEPTED ||
    controllerDetails.statusCode === ControllerStatus.ACKNOWLEDGED_WITH_ESCALATION_ACCEPTED ||
    controllerDetails.statusCode === ControllerStatus.ESCALATION_REJECTED ||
    controllerDetails.statusCode === ControllerStatus.ACKNOWLEDGED_WITH_ESCALATION_REJECTED
  );
};

export const isCheckAnswered = (
  controllerDetails: ControllerDetails,
): controllerDetails is ControllerDetailsWithResponse => {
  return (
    controllerDetails.statusCode === ControllerStatus.ACKNOWLEDGED_WITH_CHECK_SUCCESS ||
    controllerDetails.statusCode === ControllerStatus.CHECK_REJECTED ||
    controllerDetails.statusCode === ControllerStatus.CHECK_SUCCESS ||
    isEscalationAnswered(controllerDetails)
  );
};

export const isExpiredAfterCheck = (controllerDetails: ControllerDetails) => {
  return hasCheckData(controllerDetails) && new Date(controllerDetails.check.expirationDateTime) < new Date();
};

export const getRequestData = (controllerDetails: ControllerDetailsStarted) => {
  const { requestData } = controllerDetails.check.controlData;
  return requestData ?? {};
};

export const getEscalationRequestData = (controllerDetails: ControllerDetails) => {
  if (!hasEscalation(controllerDetails)) return {};
  const { requestData } = controllerDetails.escalation.controlData;
  return requestData ?? {};
};

export const isManualCheckRequired = (controllerDetails: ControllerDetails) => {
  return controllerDetails.statusCode === ControllerStatus.MANUAL_CHECK_REQUIRED;
};
